import { FunctionComponent, ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Checkbox } from "@/components/Checkbox/Checkbox";

import { ListPersonGlobalSearchInfoDTO } from "@/pages/Private/redux/search/search.schema";
import { toListPerson } from "@/pages/Private/pages/Contacts/utils/toListPerson";

import { renderCurrentScoreResult } from "../../../List/utils/allColumns";
import { LeadName } from "../../../List/components/CampaignOverview/components/Lead/components/LeadName";
import { JobTitle } from "../../../List/components/CampaignOverview/components/Lead/components/JobTitle";
import { OrgName } from "../../../List/components/CampaignOverview/components/Lead/components/OrgName";
import { OutboxSenderInfo } from "../../../List/components/CampaignOverview/components/Lead/components/OutboxSenderInfo";
import { StepFailureTag } from "../../../List/components/CampaignOverview/components/Lead/components/StepFailureTag";
import { LeadHoldTag } from "../../../List/components/CampaignOverview/components/Lead/components/LeadHoldTag";
import { CardChannelBadge } from "./CardChannelBadge";
import { LeadCardCompact } from "./LeadCardCompact";
import { LeadStatusChip } from "./LeadStatusChip";
import {
	getCardChannel,
	getCardProvenance,
	getLastOutboundAuthor,
	getLeadScore,
	getSenderFromConversations,
} from "./lead-card-compact.helper";
import { resolveContactStatus } from "./contact-card-status.helper";

interface ContactLeadCompactProps {
	contact: ListPersonGlobalSearchInfoDTO;
	/** Opens the contact (Find a person navigates to the lead). */
	onOpen: () => void;
	/** Selected prospect ids. The checkbox is shown only when a setter is given. */
	selectedProspectIds?: number[];
	setSelectedProspectIds?: (ids: number[]) => void;
	/** Start of today, for the Outbox bucket of a lead in outreach. */
	todayStart?: Date;
	/** Rows below the card body, e.g. "Add to outreach". */
	footer?: ReactNode;
}

/**
 * An existing contact in Find a person, on the Inbox/Outbox compact card (PREC-4175): score,
 * channel and sender in the gutter; name, title and company stacked; the list and campaign it came
 * from; and where the lead stands. Everything else is in the lead panel. It replaces the list-row
 * `Lead`, whose two half-width columns ran past the right edge of the results column.
 */
export const ContactLeadCompact: FunctionComponent<ContactLeadCompactProps> = ({
	contact,
	onOpen,
	selectedProspectIds,
	setSelectedProspectIds,
	todayStart,
	footer,
}) => {
	const { t } = useTranslation();
	const row = useMemo(() => toListPerson(contact), [contact]);
	const status = useMemo(
		() => resolveContactStatus(row, todayStart ?? startOfToday()),
		[row, todayStart]
	);
	const provenance = getCardProvenance(row);
	const hasOutreach = !!row.campaignId || (row.schedules?.length ?? 0) > 0;
	const prospectId = row.prospect?.id;

	const toggleSelected = () => {
		if (!setSelectedProspectIds || prospectId === undefined) {
			return;
		}

		const current = selectedProspectIds ?? [];

		setSelectedProspectIds(
			current.includes(prospectId)
				? current.filter((id) => id !== prospectId)
				: [...current, prospectId]
		);
	};

	return (
		<LeadCardCompact
			checkbox={
				setSelectedProspectIds && prospectId !== undefined ? (
					<Checkbox
						isChecked={selectedProspectIds?.includes(prospectId) ?? false}
						onChange={(event) => {
							event.stopPropagation();
							toggleSelected();
						}}
					/>
				) : undefined
			}
			chips={
				<>
					{status?.kind === "failure" && <StepFailureTag failure={status.failure} t={t} />}
					{status?.kind === "chip" && (
						<LeadStatusChip label={t(status.chip.labelKey)} tone={status.chip.tone} />
					)}
					<LeadHoldTag schedules={row.schedules} />
				</>
			}
			company={<OrgName compact row={row} />}
			footer={footer}
			gutter={
				<>
					{renderCurrentScoreResult({
						scoring: row.prospect?.scorePersonaCategoryResult,
						score: getLeadScore(row),
					})}
					<CardChannelBadge channel={getCardChannel(row)} />
					<OutboxSenderInfo
						fallbackSender={getSenderFromConversations(row)}
						hideWhenUnknown
						lastOutboundAuthor={getLastOutboundAuthor(row)}
						readOnly
						row={row}
					/>
				</>
			}
			isActive={false}
			name={<LeadName row={row} />}
			provenance={
				row.list.id
					? {
							listName: provenance.listName,
							listTo: provenance.listTo,
							campaignLabel:
								provenance.campaignName ?? (hasOutreach ? t("outbox.card.chatOutreach") : null),
						}
					: null
			}
			title={<JobTitle compact row={row} />}
			onClick={onOpen}
		/>
	);
};

const startOfToday = (): Date => {
	const today = new Date();

	today.setHours(0, 0, 0, 0);

	return today;
};
